import React, { useState } from "react";
import axiosInstance from "../services/axiosinstance";
import Notiflix from "notiflix";
import { apiUrl } from "../routes/apiRoutes";

function useCourseForm(initialData, isEditing = false, onSave) {
  const [dragActive, setDragActive] = useState(false);
  const [formData, setFormData] = useState({
    courseName: initialData?.courseName || "",
    description: initialData?.description || "",
    duration: initialData?.duration || "",
    price: initialData?.price ?? "0",
    isPaid: initialData?.isPaid ?? true,
    isLocked: initialData?.isLocked ?? false,
    category: initialData?.category || "",
    programType: initialData?.programType || "",
    slug: initialData?.slug || "",
    level: initialData?.level || "Beginner",
    instructor: initialData?.instructor || "Nameet Mandwal",
    tags: initialData?.tags || [],
    lectures: initialData?.lectures || [],
    mode: initialData?.mode || "online",
    sampleVideoUrl: initialData?.sampleVideoUrl || "",
    isFeaturedOnHome: initialData?.isFeaturedOnHome || false,
    poster: initialData?.poster
      ? typeof initialData.poster === "string"
        ? { url: initialData.poster }
        : initialData.poster
      : null,
  });
  // console.log("id",initialData._id)

  const [currentTag, setCurrentTag] = useState("");
  const [lectureData, setLectureData] = useState({
    name: "",
    videoLink: "",
    videoFile: null,
    videoType: "link",
    topics: [],
    duration: "",
    description: "",
  });
  const [currentTopic, setCurrentTopic] = useState("");
  const [showLectureForm, setShowLectureForm] = useState(false);
  const [editingLecture, setEditingLecture] = useState(null);

  const categories = [
    "Web Development",
    "Mobile Development",
    "Data Science",
    "Backend Development",
    // "AI & Machine Learning",
    // "DevOps",
    // "UI/UX Design",
    // "Cybersecurity",
    // "Cloud Computing",
    // "Blockchain",
  ];

  const programTypeList = [
    "short-term",
    "graduate-program",
    "dual-certification",
    "classroom",
    "master-program",
  ];

  const levels = ["Beginner", "Intermediate", "Advanced", "Expert"];
  const modes = ["online", "offline"];

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleLectureInputChange = (field, value) => {
    setLectureData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleVideoUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check if file is a video
      if (!file.type.startsWith("video/")) {
        alert("Please select a valid video file");
        return;
      }

      // Check file size (limit to 500MB)
      if (file.size > 500 * 1024 * 1024) {
        alert("Video file size should be less than 500MB");
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setLectureData((prev) => ({
          ...prev,
          videoFile: {
            file,
            url: e.target?.result,
            name: file.name,
            size: file.size,
          },
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleVideoTypeChange = (type) => {
    setLectureData((prev) => ({
      ...prev,
      videoType: type,
      videoLink: type === "upload" ? "" : prev.videoLink,
      videoFile: type === "link" ? null : prev.videoFile,
    }));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData((prev) => ({
          ...prev,
          poster: {
            file,
            url: e.target?.result,
            name: file.name,
          },
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData((prev) => ({
          ...prev,
          poster: {
            file,
            url: e.target?.result,
            name: file.name,
          },
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const addTag = () => {
    if (currentTag.trim() && !formData.tags.includes(currentTag.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, currentTag.trim()],
      }));
      setCurrentTag("");
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const addTopic = () => {
    if (
      currentTopic.trim() &&
      !lectureData.topics.includes(currentTopic.trim())
    ) {
      setLectureData((prev) => ({
        ...prev,
        topics: [...prev.topics, currentTopic.trim()],
      }));
      setCurrentTopic("");
    }
  };

  const removeTopic = (topicToRemove) => {
    setLectureData((prev) => ({
      ...prev,
      topics: prev.topics.filter((topic) => topic !== topicToRemove),
    }));
  };

  const handleSaveLecture = () => {
    if (
      !lectureData.name ||
      (!lectureData.videoLink && !lectureData.videoFile)
    ) {
      alert(
        "Please fill in lecture name and provide either a video link or upload a video file",
      );
      // console.log(lectureData);
      return;
    }

    const newLecture = {
      ...lectureData,
      id: editingLecture ? editingLecture.id : Date.now(),
      createdAt: editingLecture
        ? editingLecture.createdAt
        : new Date().toISOString(),
    };

    if (editingLecture) {
      setFormData((prev) => ({
        ...prev,
        lectures: prev.lectures.map((lecture) =>
          lecture.id === editingLecture.id ? newLecture : lecture,
        ),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        lectures: [...prev.lectures, newLecture],
      }));
    }

    // Reset lecture form
    setLectureData({
      name: "",
      videoLink: "",
      videoFile: null,
      videoType: "link",
      topics: [],
      duration: "",
      description: "",
    });
    setShowLectureForm(false);
    setEditingLecture(null);
  };

  const handleEditLecture = (lecture) => {
    setLectureData(lecture);
    setEditingLecture(lecture);
    setShowLectureForm(true);
  };

  const handleDeleteLecture = (lectureId) => {
    if (window.confirm("Are you sure you want to delete this lecture?")) {
      setFormData((prev) => ({
        ...prev,
        lectures: prev.lectures.filter((lecture) => lecture.id !== lectureId),
      }));
    }
  };

  const handleCancelLecture = () => {
    setLectureData({
      name: "",
      videoLink: "",
      videoFile: null,
      videoType: "link",
      topics: [],
      duration: "",
      description: "",
    });
    setShowLectureForm(false);
    setEditingLecture(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    Notiflix.Loading.dots();
    // console.log(formData);

    // Build a REAL FormData object — sending a plain JS object with a
    // "multipart/form-data" header (as before) doesn't actually multipart-
    // encode it; axios just JSON.stringifies it regardless of the header,
    // and multer on the backend can't parse a JSON body as multipart. This
    // silently dropped/garbled fields (price, isFeaturedOnHome, tags, etc.)
    // depending on how multer/body-parser handled the mismatch.
    const payload = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      if (key === "poster") return; // handled separately below
      if (key === "lectures") return; // not part of course creation payload
      if (value === undefined || value === null) return;
      if (Array.isArray(value)) {
        // tags: send as repeated fields so multer/body-parser gives an array
        value.forEach((v) => payload.append(key, v));
      } else if (typeof value === "boolean") {
        payload.append(key, value ? "true" : "false");
      } else {
        payload.append(key, value);
      }
    });
    if (formData.poster?.file) {
      payload.append("courseposter", formData.poster.file);
    }

    try {
      if (isEditing) {
        await axiosInstance.patch(
          `${apiUrl.UPDATECOURSE}/${initialData?._id}`,
          payload,
          {
            headers: {
              "Content-Type": "multipart/form-data", // 👈 very important
            },
          },
        );
      } else {
        await axiosInstance.post(apiUrl.POSTCOURSE, payload, {
          headers: {
            "Content-Type": "multipart/form-data", // 👈 very important
          },
        });
      }
      setFormData({
        courseName: "",
        description: "",
        duration: "",
        price: "0",
        slug: "",
        programType: "",
        isPaid: true,
        isLocked: false,
        category: "",
        level: "Beginner",
        instructor: "Nameet Mandwal",
        tags: [],
        lectures: [],
        mode: "online",
        poster: undefined,
      });
      if (onSave) {
        onSave();
      }
      Notiflix.Loading.remove();
    } catch (error) {
      // console.log(error);
      Notiflix.Loading.remove();
    }
  };

  const totalDuration = formData.lectures.reduce(
    (sum, lecture) => sum + Number(lecture.duration || 0),
    0,
  );

  return {
    dragActive,
    formData,
    setFormData,
    categories,
    levels,
    modes,
    handleInputChange,
    handleLectureInputChange,
    handleVideoUpload,
    handleVideoTypeChange,
    formatFileSize,
    handleImageUpload,
    handleDrag,
    handleDrop,
    addTag,
    removeTag,
    addTopic,
    removeTopic,
    handleSaveLecture,
    handleEditLecture,
    handleDeleteLecture,
    handleCancelLecture,
    handleSubmit,
    setCurrentTag,
    totalDuration,
    programTypeList,
  };
}

export default useCourseForm;
